import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, FileText, Plus, Send, Upload } from 'lucide-react';
import Panel from './Panel.jsx';
import { Button } from '@/components/ui/button.jsx';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import {
  listDealInterests, createInterest, updateInterest, endInterest,
  sendInterestCert, uploadInterestCert, confirmInterestForTerm,
} from '@/services/interests.service.js';
import COISheet from '@/components/sheets/COISheet.jsx';
import { openStoredDocument } from '@/lib/openDocument.js';
import { DOCUMENTS_CHANGED_EVENT } from '@/services/deals.service.js';
import { CcField, ccList, sendWord } from '@/components/paperwork/sendSheetParts.jsx';

// ADDITIONAL-INTERESTS-SPEC Part C2. The record-keeping surface for
// additional interests: carrier-issued cert drop target + send + soft end.
// Status chips are DERIVED (the certificate on file in the documents home / its
// delivered send in document_sends, paperwork-1 L3-2), never a manual state machine. Generate ACORD is the demoted escape hatch for the
// rare lender who insists on an ACORD form.

// Full DB vocabulary (pinned to TYPE_LABELS in additional-interests.service.js
// by tests/interest-type-labels.test.js — this panel was missing 3 legal types).
const TYPE_OPTIONS = [
  ['additional_insured', 'Additional Insured'],
  ['loss_payee', 'Loss Payee'],
  ['additional_insured_loss_payee', 'Additional Insured & Loss Payee'],
  ['lienholder', 'Lienholder'],
  ['mortgagee', 'Mortgagee'],
  ['lenders_loss_payable', 'Lenders Loss Payable'],
  ['owner_lessor', 'Owner/Lessor'],
  ['airport_authority', 'Airport Authority'],
  ['storage_facility', 'Storage Facility'],
  ['dual_instruction_flight_school', 'Dual Instruction/Flight School'],
  ['formal_flight_training_facility', 'Formal Flight Training Facility'],
  ['insured_pilot_employer', 'Insured/Pilot Employer'],
  ['charity_organization', 'Charity Organization'],
  ['savvy_aviation', 'Savvy Aviation Inc. (SavvyMx)'],
  ['dd2400', 'DD2400'],
  ['dry_lessee', 'Dry Lessee'],
];

// A lien-family interest is secured by a specific airframe (server enforces
// the same rule; this is the friendly copy of it).
const AIRCRAFT_REQUIRED = new Set(['lienholder', 'owner_lessor', 'loss_payee', 'mortgagee', 'lenders_loss_payable', 'dry_lessee']);

const inputCls = 'min-h-[34px] w-full rounded-card border border-border bg-bg px-2.5 py-1 text-sm text-text placeholder:text-text-dim focus:border-accent focus:outline-none';

function emptyForm() {
  return { name: '', interest_type: 'lienholder', attention: '', contact_first_name: '', contact_preferred_name: '', street1: '', street2: '', city: '', state: '', zip: '', email: '', phone: '', aircraft_tail: '', effective_date: '', expiration_date: '', notes: '' };
}

// N9 (David 2026-11-01): the contact person the holder's email greets ("Hi <preferred name>," else first name, else "Hello,").
// A key the broker did not change is left out of the save (the server reads an absent key as "not stated"), so a save that
// never touched the names writes nothing to them.
const CONTACT_KEYS = ['contact_first_name', 'contact_preferred_name'];
function withChangedContact(form, before = {}) {
  const out = { ...form };
  for (const k of CONTACT_KEYS) if (String(out[k] || '').trim() === String(before[k] || '').trim()) delete out[k];
  return out;
}

function StatusChip({ interest }) {
  if (interest.cert_sent_at) {
    return <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-success"><span className="h-[7px] w-[7px] rounded-full bg-success" />Sent {String(interest.cert_sent_at).slice(0, 10)}</span>;
  }
  if (interest.cert_on_file) {
    // A send that did not reach the holder (a test address, a failure, one from before tracking) is said beside the chip.
    const last = interest.cert_last_send;
    return (
      <span className="inline-flex flex-col gap-0.5">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-info"><span className="h-[7px] w-[7px] rounded-full bg-info" />Cert on file</span>
        {last && !last.delivered ? <span className="text-[10px] text-text-dim" data-cert-last-send>Last send: {sendWord(last)}</span> : null}
      </span>
    );
  }
  return <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-warning"><span className="h-[7px] w-[7px] rounded-full bg-warning" />No cert on file</span>;
}

// propertyScope: the deal is hangar/property paper (server-reported), so the
// interest is secured by the premises and there is no tail to ask for.
// tails: the deal's aircraft, from the same set the server uses to decide which
// interests print. Empty means the server could not resolve them (both reads are
// non-blocking), not that the deal has no aircraft, so the form falls back to
// free text rather than offering nothing. A stored value outside the list (an
// orphaned tail, a comma-joined multi-tail row) stays selectable as-is, so an
// edit never silently rewrites it.
function InterestForm({ form, setForm, propertyScope = false, tails = [] }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const required = AIRCRAFT_REQUIRED.has(form.interest_type);
  const current = (form.aircraft_tail || '').trim();
  const offCurrent = current && !tails.includes(current.toUpperCase()) ? current : null;
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <input className={`${inputCls} sm:col-span-2`} placeholder="Name (bank, lessor, authority...)" value={form.name} onChange={set('name')} />
      <select className={inputCls} value={form.interest_type} onChange={set('interest_type')}>
        {TYPE_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
      {propertyScope ? (
        <div className="flex min-h-[34px] items-center text-[12px] text-text-dim">Secured by the insured premises (property paper only)</div>
      ) : tails.length ? (
        <select
          className={inputCls}
          value={offCurrent ? current : current.toUpperCase()}
          onChange={set('aircraft_tail')}
        >
          <option value="">{required ? 'Select aircraft (required for this type)' : 'All aircraft'}</option>
          {tails.map((t) => <option key={t} value={t}>{t}</option>)}
          {offCurrent && <option value={offCurrent}>{offCurrent} (not on this policy)</option>}
        </select>
      ) : (
        <input
          className={inputCls}
          placeholder={required ? 'Aircraft tail (required for this type)' : 'Aircraft tail (blank = all)'}
          value={form.aircraft_tail}
          onChange={set('aircraft_tail')}
        />
      )}
      <input className={inputCls} placeholder="Attention (optional)" value={form.attention} onChange={set('attention')} />
      <div className="grid grid-cols-2 gap-2 sm:col-span-2" data-interest-contact>
        <label className="text-[11px] text-text-dim">Contact person's first name
          <input className={inputCls} value={form.contact_first_name || ''} onChange={set('contact_first_name')} data-contact-first-name />
        </label>
        <label className="text-[11px] text-text-dim">Name they go by
          <input className={inputCls} value={form.contact_preferred_name || ''} onChange={set('contact_preferred_name')} data-contact-preferred-name />
        </label>
        <p className="col-span-2 text-[11px] text-text-dim">The certificate email greets them by the name they go by, else the first name, else "Hello,".</p>
      </div>
      <div>
        <label className="mb-0.5 block text-[11px] text-text-dim">Effective date (when the interest attaches)</label>
        <input type="date" className={inputCls} value={form.effective_date || ''} onChange={set('effective_date')} />
      </div>
      <div>
        <label className="mb-0.5 block text-[11px] text-text-dim">Expiration (blank = runs with the policy)</label>
        <input type="date" className={inputCls} value={form.expiration_date || ''} onChange={set('expiration_date')} />
      </div>
      <input className={inputCls} placeholder="Email" value={form.email} onChange={set('email')} />
      <input className={`${inputCls} sm:col-span-2`} placeholder="Street" value={form.street1} onChange={set('street1')} />
      <input className={inputCls} placeholder="City" value={form.city} onChange={set('city')} />
      <div className="flex gap-2">
        <input className={inputCls} placeholder="State" maxLength={2} value={form.state} onChange={set('state')} />
        <input className={inputCls} placeholder="Zip" value={form.zip} onChange={set('zip')} />
      </div>
      <input className={inputCls} placeholder="Phone (optional)" value={form.phone} onChange={set('phone')} />
      <input className={inputCls} placeholder="Internal notes (never client-facing)" value={form.notes} onChange={set('notes')} />
    </div>
  );
}

export default function AdditionalInterestsPanel({ dealId, accountId, onChanged }) {
  const [certDragId, setCertDragId] = useState(null);
  const [rows, setRows] = useState([]);
  const [orphaned, setOrphaned] = useState([]);
  const [dealScope, setDealScope] = useState('aircraft');
  const [dealTails, setDealTails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [endTarget, setEndTarget] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  // "Add others (CC)" on the certificate's email (round 2, Q21), cleared with each new send.
  const [sendCc, setSendCc] = useState('');
  const [acordOpen, setAcordOpen] = useState(false);
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  const load = useCallback(() => {
    if (!dealId) return;
    setLoading(true);
    listDealInterests(dealId)
      .then((r) => { setRows(r?.interests || []); setOrphaned(r?.orphaned || []); setDealScope(r?.scope_kind === 'property' ? 'property' : 'aircraft'); setDealTails(Array.isArray(r?.tails) ? r.tails : []); })
      .catch(() => { setRows([]); setOrphaned([]); setDealTails([]); })
      .finally(() => setLoading(false));
  }, [dealId]);
  useEffect(() => { load(); }, [load]);
  // A certificate filed from the page-wide drop re-reads the rows (their "Cert on file" is the documents home's).
  useEffect(() => {
    const on = (ev) => { if (String(ev?.detail?.dealId) === String(dealId)) load(); };
    window.addEventListener(DOCUMENTS_CHANGED_EVENT, on);
    return () => window.removeEventListener(DOCUMENTS_CHANGED_EVENT, on);
  }, [dealId, load]);

  const notify = useCallback(() => { load(); if (typeof onChanged === 'function') onChanged(); }, [load, onChanged]);

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.effective_date) return toast.error('Effective date is required (when the interest attaches)');
    const propertyScope = dealScope === 'property';
    if (!propertyScope && AIRCRAFT_REQUIRED.has(form.interest_type) && !form.aircraft_tail.trim()) {
      return toast.error('Aircraft tail is required: this interest type is secured by a specific aircraft');
    }
    setBusy(true);
    try {
      await createInterest({ ...withChangedContact(form), aircraft_tail: propertyScope ? '' : form.aircraft_tail, scope_kind: dealScope, crm_account_id: accountId });
      toast.success(`${form.name.trim()} added`);
      setAddOpen(false);
      setForm(emptyForm());
      notify();
    } catch (e) {
      toast.error(e?.body?.error || e?.message || 'Add failed');
    } finally { setBusy(false); }
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    setBusy(true);
    try {
      await updateInterest(editTarget.id, withChangedContact(form, editTarget));
      toast.success(`${form.name.trim() || editTarget.name} updated`);
      setEditTarget(null);
      notify();
    } catch (e) {
      toast.error(e?.body?.error || e?.message || 'Update failed');
    } finally { setBusy(false); }
  };

  const handleEnd = async () => {
    if (!endTarget) return;
    setBusy(true);
    try {
      await endInterest(endTarget.id, 'ended by broker');
      toast.success(`${endTarget.name} ended. It will no longer appear on policy paper.`);
      setEndTarget(null);
      notify();
    } catch (e) {
      toast.error(e?.body?.error || e?.message || 'End failed');
    } finally { setBusy(false); }
  };

  const handleSend = async () => {
    if (!sendTarget) return;
    setBusy(true);
    try {
      // The one send path (paperwork-1 L3-2): the certificate attached, recorded in document_sends, read back.
      const r = await sendInterestCert(sendTarget.id, dealId, ccList(sendCc));
      if (r?.send?.delivered) toast.success(`${r.document?.name || 'Certificate'} sent to ${r.sent_to}`);
      else toast.info(`${r?.document?.name || 'Certificate'}: ${sendWord(r?.send)}`);
      // Q21: what the server says it copied (never an echo of the field).
      if (r?.cc?.length) toast.info(r.cc_dropped_by_override ? `Not copied, the test address override is on: ${r.cc.join(', ')}` : `Copied: ${r.cc.join(', ')}`);
      setSendTarget(null); setSendCc('');
      notify();
    } catch (e) {
      toast.error(e?.body?.error || e?.message || 'Send failed');
    } finally { setBusy(false); }
  };

  // §12: recording that David looked, this term. Never blocking, no evidence
  // implied beyond "a broker reviewed this row for this term".
  const handleConfirmTerm = async (r) => {
    setBusy(true);
    try {
      await confirmInterestForTerm(r.id, dealId);
      toast.success(`${r.name} confirmed for this term`);
      notify();
    } catch (e) {
      toast.error(e?.body?.error || e?.message || 'Confirm failed');
    } finally { setBusy(false); }
  };

  const startUpload = (interest) => {
    uploadTargetRef.current = interest;
    fileInputRef.current?.click();
  };
  const handleFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const interest = uploadTargetRef.current;
    if (!file || !interest) return;
    setBusy(true);
    try {
      await fileCert(interest, file);
      notify();
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally { setBusy(false); }
  };

  // One certificate through the deal's document door; the replace question, when the server asks it, opens the dialog below.
  const [replaceAsk, setReplaceAsk] = useState(null); // { interest, file, candidates }
  const fileCert = async (interest, file, replaces) => {
    const res = await uploadInterestCert(interest.id, dealId, file, { replaces });
    if (res.ask) { setReplaceAsk({ interest, file, candidates: res.ask }); return false; }
    toast.success(`Certificate filed for ${interest.name} as ${res.document?.name || file.name}`);
    return true;
  };
  const answerReplace = async (replaces) => {
    const a = replaceAsk;
    setReplaceAsk(null);
    if (!a) return;
    setBusy(true);
    try { if (await fileCert(a.interest, a.file, replaces)) notify(); }
    catch (err) { toast.error(`${a.file.name}: ${err?.message || 'upload failed'}`); }
    finally { setBusy(false); }
  };

  const openEdit = (r) => {
    setForm({
      name: r.name, interest_type: r.interest_type, attention: r.attention || '',
      contact_first_name: r.contact_first_name || '', contact_preferred_name: r.contact_preferred_name || '',
      // Each line from its own field. This read `r.street` (line 1 and line 2
      // already joined) into street1 and hardcoded street2 empty, so opening
      // Edit on an interest with a second address line and saving collapsed
      // the two into one and blanked the second.
      street1: r.street1 || '', street2: r.street2 || '', city: r.city || '', state: r.state || '', zip: r.zip || '',
      email: r.email || '', phone: r.phone || '',
      aircraft_tail: r.aircraft === 'All' ? '' : r.aircraft,
      effective_date: r.effective_date || '', expiration_date: r.expiration_date || '',
      notes: r.notes || '',
    });
    setEditTarget(r);
  };

  // Prompt 173 §2a: the interest row is a DECLARED drop zone — the Cert
  // button has invited "drop the carrier-issued certificate PDF" since it
  // shipped, so a drop here files against THIS interest and never bubbles to
  // the workspace router (let alone the quote parser).
  const handleCertDrop = async (r, e) => {
    if (![...(e.dataTransfer?.types || [])].includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    const all = [...(e.dataTransfer.files || [])];
    const pdfs = all.filter((f) => f.type === 'application/pdf');
    if (all.length > pdfs.length) toast.error(`${all.length - pdfs.length} non-PDF file(s) skipped; certificates file as PDF`);
    for (const f of pdfs) {
      try {
        // The deal's document door, kind certificate naming this interest (uploadInterestCert); a replace question stops here.
        if (!(await fileCert(r, f))) break;
      } catch (err) {
        toast.error(`${f.name}: ${err?.message || 'upload failed'}`);
      }
    }
    if (pdfs.length) notify();
  };

  const renderRow = (r, { orphan = false } = {}) => (
    <div
      key={r.id}
      className={`border-t border-border p-3 text-[13px] first:border-t-0 ${orphan ? 'bg-warning-soft/40' : ''} ${certDragId === r.id ? 'ring-2 ring-accent bg-accent-soft/30' : ''}`}
      onDragOver={(e) => {
        if ([...(e.dataTransfer?.types || [])].includes('Files')) { e.preventDefault(); e.stopPropagation(); }
      }}
      // 198 §1.1: this row CLAIMS the drop with stopPropagation and showed
      // nothing for it, so the page banner was the only feedback and it named
      // the fallback rather than this row. A zone that catches a file says so.
      onDragEnter={(e) => {
        if ([...(e.dataTransfer?.types || [])].includes('Files')) { e.preventDefault(); e.stopPropagation(); setCertDragId(r.id); }
      }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setCertDragId(null); }}
      onDrop={(e) => { setCertDragId(null); handleCertDrop(r, e); }}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <button type="button" onClick={() => setExpanded(expanded === r.id ? null : r.id)} className="flex flex-1 items-center gap-2 text-left">
          {expanded === r.id ? <ChevronDown size={13} strokeWidth={2} className="shrink-0 text-text-dim" /> : <ChevronRight size={13} strokeWidth={2} className="shrink-0 text-text-dim" />}
          <span className="font-medium">{r.name}</span>
          <span className="text-[11px] font-normal text-text-dim">· {r.type}{r.aircraft && r.aircraft !== 'All' ? ` · ${r.aircraft}` : ''}</span>
          {!orphan && (r.confirmed_this_term
            ? <span className="text-[10px] font-medium text-success">confirmed this term</span>
            : <span className="text-[10px] font-medium italic text-text-dim">carried, unconfirmed</span>)}
        </button>
        <div className="sm:w-32">
          {orphan
            ? <span className="text-[11px] font-medium text-warning">Aircraft not on current policy</span>
            : <StatusChip interest={r} />}
        </div>
        <div className="flex items-center gap-2 sm:justify-end">
          {/* paperwork-1 L1b-B3: the certificate on file is the documents home's, opened by its id through the download door. */}
          {r.cert_document_id ? (
            <button type="button" onClick={() => openStoredDocument(r.cert_document_id)} title={r.cert_filename || undefined} className="rounded-md border border-border-strong px-2.5 py-1 text-[11px] text-text hover:bg-surface-strong" data-open-certificate={r.cert_document_id}>Open certificate</button>
          ) : null}
          <button type="button" disabled={busy} onClick={() => startUpload(r)} className="rounded-md border border-border-strong px-2.5 py-1 text-[11px] text-text-muted hover:bg-surface-strong hover:text-text" title="Drop the carrier-issued certificate PDF">
            <Upload size={12} strokeWidth={1.7} className="mr-1 inline" />Cert
          </button>
          {r.cert_on_file ? (
            <button type="button" disabled={busy} onClick={() => setSendTarget(r)} className="rounded-md border border-accent bg-accent px-2.5 py-1 text-[11px] text-white hover:brightness-110">
              <Send size={12} strokeWidth={2} className="mr-1 inline" />{r.cert_sent_at ? 'Resend' : 'Send'}
            </button>
          ) : null}
        </div>
      </div>
      {expanded === r.id ? (
        <div className="mt-2 flex flex-col gap-1 pl-6 text-xs text-text-muted">
          {r.address ? <span>{r.address}</span> : <span className="italic text-text-dim">No address on file</span>}
          {r.email ? <span>{r.email}</span> : null}
          {r.cert_filename ? <span className="text-text-dim"><FileText size={11} strokeWidth={1.5} className="mr-1 inline" />{r.cert_filename}</span> : null}
          <div className="mt-1 flex items-center gap-3">
            {!r.confirmed_this_term ? (
              <button type="button" disabled={busy} className="text-[11px] text-success hover:underline" onClick={() => handleConfirmTerm(r)} title="Records that you reviewed this interest for this term. Never blocks anything.">Confirm for this term</button>
            ) : null}
            <button type="button" className="text-[11px] text-info hover:underline" onClick={() => openEdit(r)}>Edit</button>
            <button type="button" className="text-[11px] text-warning hover:underline" onClick={() => setEndTarget(r)}>End</button>
            <button type="button" className="text-[11px] text-text-dim hover:text-text-muted hover:underline" onClick={() => setAcordOpen(true)} title="For the rare lender who insists on an ACORD form">Generate ACORD</button>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <Panel
      title="Additional interests"
      count={rows.length || null}
      empty={!loading && rows.length === 0 && orphaned.length === 0
        ? 'No additional interests on this account. Add lienholders, lessors, and anyone else who needs proof of coverage.'
        : null}
      footer={loading ? <span className="text-text-dim">Loading…</span> : null}
    >
      <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={handleFilePicked} />
      {rows.length > 0 || orphaned.length > 0 ? (
        <div className="overflow-hidden rounded-card border border-border">
          {rows.map((r) => renderRow(r))}
          {orphaned.map((r) => renderRow(r, { orphan: true }))}
        </div>
      ) : null}
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={busy || !accountId} onClick={() => { setForm(emptyForm()); setAddOpen(true); }} className="gap-1.5">
          <Plus size={14} strokeWidth={2} />Add interest
        </Button>
      </div>

      {/* Add */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add additional interest</DialogTitle>
            <DialogDescription>Lienholder, lessor, airport authority, or anyone else who needs proof of coverage. Carries to future terms until ended.</DialogDescription>
          </DialogHeader>
          <InterestForm form={form} setForm={setForm} propertyScope={dealScope === 'property'} tails={dealTails} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button variant="accent" disabled={busy} onClick={handleCreate}>Add interest</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editTarget} onOpenChange={(v) => !v && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {editTarget?.name}</DialogTitle>
          </DialogHeader>
          <InterestForm form={form} setForm={setForm} propertyScope={dealScope === 'property'} tails={dealTails} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button variant="accent" disabled={busy} onClick={handleEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End (soft) */}
      <Dialog open={!!endTarget} onOpenChange={(v) => !v && setEndTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>End {endTarget?.name}?</DialogTitle>
            <DialogDescription>
              The interest stops appearing on policy paper and future terms. The record is kept for history.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEndTarget(null)}>Cancel</Button>
            <Button variant="accent" disabled={busy} onClick={handleEnd}>End interest</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send confirm — this emails an external party, always confirm first */}
      <Dialog open={!!sendTarget} onOpenChange={(v) => { if (!v) { setSendTarget(null); setSendCc(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send certificate to {sendTarget?.name}?</DialogTitle>
            <DialogDescription>
              {sendTarget?.email
                ? `Emails ${sendTarget.cert_filename || 'the filed certificate'}, attached, to ${sendTarget.email}.`
                : 'This interest has no email on file. Add one via Edit first.'}
            </DialogDescription>
          </DialogHeader>
          {sendTarget?.email ? <CcField value={sendCc} onChange={setSendCc} to={sendTarget.name || sendTarget.email} disabled={busy} /> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSendTarget(null); setSendCc(''); }}>Cancel</Button>
            <Button variant="accent" className="h-auto whitespace-normal" disabled={busy || !sendTarget?.email} onClick={handleSend}>Send the certificate to {sendTarget?.name}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* The replace question (paperwork-1 D7): asked, never inferred */}
      <Dialog open={!!replaceAsk} onOpenChange={(v) => !v && setReplaceAsk(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Does {replaceAsk?.file?.name} replace the certificate already filed for {replaceAsk?.interest?.name}?</DialogTitle>
            <DialogDescription>A replaced certificate is kept, marked as replaced, and is never sent again.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(replaceAsk?.candidates || []).map((c) => (
              <Button key={c.id} variant="outline" className="h-auto w-full justify-start whitespace-normal py-2 text-left" disabled={busy} onClick={() => answerReplace(c.id)}>Yes, it replaces {c.name}</Button>
            ))}
          </div>
          {/* The answers wrap inside the dialog: a derived name is long (L1b-B4, Chrome pass). */}
          <DialogFooter className="sm:flex-wrap">
            <Button variant="outline" onClick={() => setReplaceAsk(null)}>Cancel (file nothing)</Button>
            <Button variant="accent" disabled={busy} onClick={() => answerReplace('none')}>No, file it as another certificate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Demoted escape hatch: ACORD 25/28 wizard */}
      <COISheet dealId={dealId} accountId={accountId} open={acordOpen} onOpenChange={setAcordOpen} onChange={notify} />
    </Panel>
  );
}
